'use client';

import { ListIcon, MagnifyingGlassIcon, ShoppingCartIcon } from '@phosphor-icons/react';
import Image from 'next/image';
import Link from 'next/link';

import HeaderFavoritesLink from './HeaderFavoritesLink';
import HeaderUserMenu from './HeaderUserMenu';
import {
  getCartAriaLabel,
  HEADER_CONTAINER_CLASS,
  ICON_SIZE,
  PRIMARY_ACTION_MOTION_CLASS,
  SECONDARY_ACTION_MOTION_CLASS,
} from '../../lib/header-constants';
import { SearchCombobox, type SearchComboboxConfig } from '@/shared/components/search-combobox';
import { cn } from '@/shared/utils/cn';
import { useNavbarContext } from './context';

interface HeaderTopBarProps {
  isCartOpen: boolean;
  isMobileOpen: boolean;
  isSearchOpen: boolean;
  logoHeight: number;
  logoHref: string;
  logoSrc: string;
  logoWidth: number;
  search: SearchComboboxConfig;
  showSearch: boolean;
  onCartOpen: () => void;
  onMobileOpen: () => void;
  onSearchOpen: () => void;
}

const HeaderTopBar = ({
  isCartOpen,
  isMobileOpen,
  isSearchOpen,
  logoHeight,
  logoHref,
  logoSrc,
  logoWidth,
  search,
  showSearch,
  onCartOpen,
  onMobileOpen,
  onSearchOpen,
}: HeaderTopBarProps) => {
  const {
    user,
    cartItemCount,
    cartLoading,
    favoritesCount,
    favoritesPulseKey,
    favoritesUrl,
    hideUserActions,
    hideLogin,
    loginUrl,
    onLogin,
    registerUrl,
  } = useNavbarContext();
  const itemCount = cartItemCount;

  return (
    <div className={cn(HEADER_CONTAINER_CLASS, 'pt-3 md:pt-3.5')}>
      <div className="border-adipa-border border-b pb-3 md:pb-3.5">
        <div
          className={cn(
            'flex items-center gap-3',
            // El grid de 3 columnas (logo | buscador | acciones) solo aplica con
            // buscador. Sin él, usamos flex con justify-between para que las
            // acciones queden ancladas a la derecha y no en la columna central.
            showSearch ? 'global-header-topbar-row md:grid md:gap-4 lg:gap-5' : 'justify-between',
          )}
        >
          {/* Anchor plano a propósito: en hosts de dominio compartido (worker)
            el home puede pertenecer al sitio legacy — una navegación SPA hacia
            fuera del app falla en silencio. */}
          <a className="shrink-0 md:justify-self-start" href={logoHref}>
            <Image
              priority
              unoptimized
              alt="ADIPA"
              className="lg:max-w-50 h-auto max-h-9 w-auto max-w-36 object-contain md:max-h-10 md:max-w-44"
              height={logoHeight}
              src={logoSrc}
              width={logoWidth}
            />
          </a>

          {showSearch && (
            <SearchCombobox
              className="hidden md:block md:w-full md:justify-self-center"
              id="global-header-search-desktop"
              search={search}
            />
          )}

          <div className="ml-auto flex items-center gap-1.5 md:ml-0 md:gap-2.5 md:justify-self-end">
            {!hideUserActions && (
              <div className="hidden items-center gap-2 lg:flex">
                {user ? (
                  <HeaderUserMenu user={user} />
                ) : (
                  <>
                    {!hideLogin && (
                      <Link
                        className={cn(
                          'rounded-adipa-lg bg-adipa-surface text-adipa-text-secondary inline-flex h-10 items-center justify-center whitespace-nowrap border border-transparent px-3.5 text-sm font-semibold',
                          SECONDARY_ACTION_MOTION_CLASS,
                        )}
                        href={loginUrl}
                        onClick={onLogin}
                      >
                        Iniciar sesión
                      </Link>
                    )}

                    <Link
                      className={cn(
                        'rounded-adipa-lg bg-adipa-primary text-adipa-primary-foreground inline-flex h-10 items-center justify-center whitespace-nowrap px-4 text-sm font-semibold',
                        PRIMARY_ACTION_MOTION_CLASS,
                      )}
                      href={registerUrl}
                    >
                      Regístrate
                    </Link>
                  </>
                )}
              </div>
            )}

            <div className="flex items-center gap-0.5 lg:ml-1">
              {showSearch && (
                <button
                  aria-expanded={isSearchOpen}
                  aria-haspopup="dialog"
                  aria-label="Abrir buscador"
                  className={cn(
                    'rounded-adipa-full text-adipa-primary inline-flex h-11 w-11 items-center justify-center md:hidden',
                    'duration-adipa-fast hover:bg-adipa-surface-lavender cursor-pointer transition ease-out motion-reduce:transition-none',
                    'active:scale-95',
                    'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
                  )}
                  type="button"
                  onClick={onSearchOpen}
                >
                  <MagnifyingGlassIcon size={ICON_SIZE} weight="bold" />
                </button>
              )}

              {!hideUserActions && user && favoritesUrl ? (
                <HeaderFavoritesLink
                  count={favoritesCount}
                  href={favoritesUrl}
                  pulseKey={favoritesPulseKey}
                />
              ) : null}

              {!hideUserActions && !hideLogin && (
                <div className="relative">
                  <button
                    aria-expanded={isCartOpen}
                    aria-haspopup="dialog"
                    aria-label={getCartAriaLabel(itemCount)}
                    className={cn(
                      'rounded-adipa-full text-adipa-primary relative inline-flex h-11 w-11 items-center justify-center',
                      'duration-adipa-fast hover:bg-adipa-surface-lavender cursor-pointer transition ease-out motion-reduce:transition-none',
                      'active:scale-95',
                      'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
                    )}
                    type="button"
                    onClick={onCartOpen}
                  >
                    <ShoppingCartIcon size={ICON_SIZE} weight="bold" />

                    {/* El puntito solo aparece cuando hay items: mientras el host
                      carga los datos del carrito el conteo es 0 y no debe verse un
                      badge "0" (mala UX). Surge al confirmarse el conteo real. */}
                    {itemCount > 0 ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'rounded-adipa-full bg-adipa-primary global-header-cart-badge-text text-adipa-primary-foreground absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center font-bold tabular-nums',
                          'duration-adipa-fast transition-transform ease-out motion-reduce:transition-none',
                          isCartOpen && 'animate-cart-badge-pop',
                        )}
                      >
                        {itemCount}
                      </span>
                    ) : null}

                    {/* Estado de carga: el host aún hidrata el carrito remoto y el
                      conteo local es 0 — un pulso neutro evita leerlo como vacío. */}
                    {itemCount === 0 && cartLoading ? (
                      <span
                        aria-hidden="true"
                        className="rounded-adipa-full bg-adipa-primary/60 absolute right-1 top-1 h-3.5 w-3.5 animate-pulse"
                      />
                    ) : null}
                  </button>
                </div>
              )}
            </div>

            <button
              aria-expanded={isMobileOpen}
              aria-label="Abrir menú"
              className={cn(
                'rounded-adipa-lg text-adipa-text-primary inline-flex h-11 w-11 items-center justify-center lg:hidden',
                'duration-adipa-base hover:bg-adipa-surface-muted hover:text-adipa-primary transition-colors ease-in-out',
                'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-2',
              )}
              type="button"
              onClick={onMobileOpen}
            >
              <ListIcon size={ICON_SIZE} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderTopBar;
